import { Modal, Button, Form, ButtonGroup } from "react-bootstrap"
import { OutputSetup } from "../../models/Models"
import { AuthContext } from "../../models/AuthContextProvider";
import { useContext, useEffect, useRef, useState } from "react";
import React from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as Icon from 'react-bootstrap-icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyToClipboard } from "react-copy-to-clipboard";

// Возвращаемся к простому интерфейсу без ID
interface Message {
    role: "user" | "model";
    content: string;
}

export function OutputAiChat(props: {
    editor: OutputSetup
    setEditor: React.Dispatch<React.SetStateAction<OutputSetup | undefined>>
    reload: () => void
}) {
    const context = useContext(AuthContext);
    const [textareaValue, setTextareaValue] = useState('');
    const [history, setHistory] = useState<Message[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [chatBottom, setChatBottom] = useState(0);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Эффект для отмены fetch-запроса при размонтировании компонента
    useEffect(() => {
        return () => { if (abortControllerRef.current) { abortControllerRef.current.abort(); } };
    }, []);

    // Эффект для автоматической прокрутки чата вниз
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [history]);

    // Эффект для изменения высоты поля ввода
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "0px";
            let maxHeight = textareaRef.current.scrollHeight;
            if (maxHeight > window.innerHeight / 2) maxHeight = window.innerHeight / 2;
            textareaRef.current.style.height = `${maxHeight}px`;
            setChatBottom(maxHeight);
        }
    }, [textareaValue]);

    // Функция для сохранения истории на сервер
    const saveHistoryToServer = async (currentHistory: Message[]) => {
        if (!props.editor.Id) return;
        try {
            const messagesToSend = currentHistory.map(({ role, content }, index) => ({
                ObjectId: props.editor.Id,
                Index: index,
                Role: role,
                Content: content
            }));
            await fetch(`/Integrations/SaveAiMessages/${props.editor.Id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(messagesToSend)
            });
        } catch (error) { console.error('Ошибка при отправке истории:', error); }
    };

    // Эффект для загрузки истории (устойчивый к StrictMode)
    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        const loadMessages = async () => {
            if (!props.editor.Id) return;
            context.setLoading(true);
            try {
                const response = await fetch(`/Integrations/LoadAiMessages/${props.editor.Id}`, { signal });
                if (!response.ok) throw new Error(`Ошибка загрузки: ${response.status}`);
                const loadedMessages: Message[] = await response.json();
                setHistory(loadedMessages);
            } catch (error: any) {
                if (error.name !== 'AbortError') console.error('Ошибка при загрузке истории:', error);
            } finally {
                if (!signal.aborted) context.setLoading(false);
            }
        };
        loadMessages();
        return () => { controller.abort(); };
    }, [props.editor.Id]);

    // Обработчики отправки и удаления сообщения
    const handleUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const content = textareaValue.trim();
        if (content) {
            const newHistory: Message[] = [...history, { role: "user", content }];
            setHistory(newHistory);
            setTextareaValue("");
            await saveHistoryToServer(newHistory);
        }
    };
    const deleteMessage = async (index: number) => {
        const newHistory = history.filter((_, i) => i !== index);
        setHistory(newHistory);
        await saveHistoryToServer(newHistory);
    };

    // ===================================================================
    //     ↓  ↓  ↓  ↓  ↓   ГЛАВНОЕ ИСПРАВЛЕНИЕ   ↓  ↓  ↓  ↓  ↓
    // ===================================================================
    const generateAiResponse = async () => {
        if (abortControllerRef.current) { abortControllerRef.current.abort(); }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsGenerating(true);
        // Запоминаем историю, которая была ДО начала генерации
        const historyBeforeStreaming = [...history];
        
        // Добавляем пустой плейсхолдер для ответа AI
        setHistory([...historyBeforeStreaming, { role: "model", content: "" }]);

        let accumulatedContent = ""; // Здесь будем копить текст ответа

        try {
            const response = await fetch('/Integrations/GenerateAi', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(historyBeforeStreaming), // Отправляем историю без плейсхолдера
                signal: controller.signal
            });

            if (!response.ok) throw new Error(`Ошибка сети: ${await response.text()}`);

            const reader = response.body?.getReader();
            if (!reader) throw new Error('Не удалось прочитать ответ сервера');

            const decoder = new TextDecoder();
            while (true) {
                const { done, value } = await reader.read();
                if (done || controller.signal.aborted) break;

                // 1. Добавляем новый кусок текста в нашу переменную
                accumulatedContent += decoder.decode(value);

                // 2. Создаем ПОЛНЫЙ новый массив истории с нуля
                const newHistory = [
                    ...historyBeforeStreaming, // Берем чистую историю
                    { role: "model" as "model", content: accumulatedContent } // Добавляем сообщение со ВСЕМ накопленным текстом
                ];
                
                // 3. Устанавливаем это новое состояние. Этот метод устойчив к StrictMode.
                setHistory(newHistory);
            }
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.log('Запрос был отменен.');
            } else {
                console.error('Ошибка при генерации ответа AI:', error);
                context.showMessage(String(error.message), true, 'lg');
            }
            // В случае любой ошибки или отмены, откатываемся к состоянию до генерации
            setHistory(historyBeforeStreaming);
        } finally {
            setIsGenerating(false);
            abortControllerRef.current = null;
            // Финальное сохранение на сервер
            const finalHistory = [...historyBeforeStreaming, { role: "model" as "model", content: accumulatedContent }];
            await saveHistoryToServer(finalHistory);
        }
    };

    return (
        <Modal show={props.editor != undefined} onHide={() => props.setEditor(undefined)} centered size="xl">
            <Modal.Header closeButton className="mt-2">
                <div>
                    <h1 className="mt-3">{props.editor.Name}
                        <span className="badge bg-secondary ms-2">{props.editor.Tag}</span>
                        <span className="badge bg-secondary ms-2">{props.editor.Id}</span>
                    </h1>
                    <h2 className="mb-4">Помощь генеративной модели</h2>
                </div>
            </Modal.Header>
            <Modal.Body ref={chatContainerRef} className="overflow-y-auto" style={{ paddingBottom: `${chatBottom + 80}px` }}>
                <div className="flex-grow-1 d-flex flex-column gap-3">
                    {/* Возвращаемся к key={index}, так как он безопасен с этой логикой */}
                    {history.map((msg, index) => (
                        <div key={index} className="mb-2">
                            <div className={`ai-chat position-relative p-2 rounded ${msg.role !== "user" ? "bg-light" : "bg-white border"}`}>
                                <ReactMarkdown
                                    components={{
                                        code(props) {
                                            const { children, className } = props;
                                            const match = /language-(\w+)/.exec(className || '');
                                            const code = String(children).replace(/\n$/, '');
                                            return match ? (
                                                <div className="position-relative">
                                                    <SyntaxHighlighter style={darcula} language={match[1]} PreTag="div">{code}</SyntaxHighlighter>
                                                    <CopyToClipboard text={code}>
                                                        <Button variant="dark" size="sm" className="position-absolute top-0 end-0 m-2 p-1 lh-1">
                                                            <Icon.Clipboard2Check />
                                                        </Button>
                                                    </CopyToClipboard>
                                                </div>
                                            ) : (<code className={className}>{children}</code>);
                                        }
                                    }} remarkPlugins={[remarkGfm]}>
                                    {msg.content}
                                </ReactMarkdown>
                                <div className="position-absolute top-0 end-0 context-buttons">
                                    <ButtonGroup size="sm">
                                        <Button variant=""><CopyToClipboard text={msg.content}><Icon.Clipboard2Check /></CopyToClipboard></Button>
                                        <Button variant="" onClick={() => deleteMessage(index)}><Icon.Trash /></Button>
                                    </ButtonGroup>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <Form onSubmit={handleUserSubmit} className="position-fixed bottom-0 start-0 p-3 w-100 pe-4 bg-body-tertiary border-top">
                    <div className="d-flex w-100 gap-2 align-items-end pe-2">
                        <Form.Control
                            as="textarea"
                            rows={1}
                            value={textareaValue}
                            onChange={(e) => setTextareaValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleUserSubmit(e as any); } }}
                            placeholder="Введите сообщение..."
                            ref={textareaRef}
                            disabled={isGenerating}
                        />
                        <Button variant="primary" type="submit" size="lg" disabled={!textareaValue.trim() || isGenerating}>Отправить</Button>
                        <Button variant="success" type="button" size="lg" onClick={generateAiResponse} disabled={isGenerating || history.length === 0 || history[history.length - 1].role !== "user"}>
                            {isGenerating ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : "Генерировать"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}